<template>
  <page-wrapper :title="title" :has-pad="true">
    <div class="wrapper">
      <div class="d-flex flex-row-reverse mb-3">
        <language-select
          v-model="language"
          @change="handleChangeLanguage"
        ></language-select>
      </div>
      <provider-editor
        v-model="form"
        :logo-url="logoUrl"
        :disabled="isEditorDisabled"
        :is-disable="isDisable"
        class="editor"
      ></provider-editor>
      <div v-if="!isEditorDisabled" class="row">
        <div class="col-3">
          <sc-button
            :disabled="isRemoving"
            variant="danger"
            @click="handleClickRemove"
          >ลบ</sc-button>
        </div>
        <div class="col-9 text-end">
        <sc-button
          variant="secondary"
          @click="handleClickExportExcel"
        >ดาวน์โหลดข้อมูลผู้ให้บริการ</sc-button>
        <sc-button :disabled="isSaving" variant="primary" @click="handleClickSave">บันทึก</sc-button>
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import LanguageSelect from "@/lib/components/LanguageSelect";
import { MessageBox } from "element-ui";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { localization } from "@/lib/mapper";
import ProviderEditor from "../components/ProviderEditor";

export default {
  components: {
    PageWrapper,
    ScButton,
    ProviderEditor,
    LanguageSelect
  },
  data() {
    const title = "รายละเอียดผู้ให้บริการ";
    const addressForm = {
      province: null,
      zipCode: null,
      amphurName: null,
      tumbonName: null,
    }

    const form = {
      name: null,
      remark: null,
      logoFile: null,
      providerType: "INDIVIDUAL",
      nameOnSap: null,
      taxId: null,
      houseNo: null,
      streetAddress: null,
      addressForm,
      mobileNumber: null
    };

    const logoUrl = null;

    return {
      title,
      form,
      logoUrl,
      language: localization.TH,
      isDisable: true,
      isSaving: false,
      isRemoving: false
    };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    }
  },
  watch: {
    form(form, oldForm) {
      if (this.form.logoFile === oldForm.logoFile) {
        return;
      }

      this.logoUrl = URL.createObjectURL(this.form.logoFile);
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      this.setupForm();
    },
    async setupForm() {
      const provider = await this.fetchProvider();

      this.form = {
        name: provider.name,
        remark: provider.remark,
        logoFile: null,
        providerType: provider.provider_type,
        nameOnSap: provider.name_on_sap,
        taxId: provider.tax_id,
        houseNo: provider.house_no,
        streetAddress: provider.street_address,
        addressForm: {
          province: provider.province,
          zipCode: provider.zip_code,
          amphurName: provider.amphur_name,
          tumbonName: provider.tumbon_name
        },
        mobileNumber: provider.mobile_number,
        isVatRegistered: provider.is_vat_registered
      };
      this.logoUrl = provider.logo_url;
    },
    async fetchProvider() {
      const { id } = this.$route.params;

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.providers.get(
              id
            )
          : await baanRueJaiClient.admin.subscriptions.providers.getLocalization(
              id,
              this.language
            );

      return response.data.result;
    },
    async handleClickSave() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        await this.updateProvider();
        this.$router.push({ name: "ProviderListPage" });
      } catch {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.isSaving = false;
      }
    },
    handleChangeLanguage() {
      this.setup();
    },
    async updateProvider() {
      const { id } = this.$route.params;
      const params = await this.getUpdateProviderParams();

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.providers.put(
              id,
              params
            )
          : await baanRueJaiClient.admin.subscriptions.providers.putLocalization(
              id,
              params,
              this.language
            );

      return response.data.result;
    },
    async getUpdateProviderParams() {
      const {
        name,
        remark,
        logoFile,
        providerType,
        nameOnSap,
        taxId,
        isVatRegistered,
        houseNo,
        addressForm,
        streetAddress,
        mobileNumber
      } = this.form;

      const params = {
        name,
        remark,
        provider_type: providerType,
        name_on_sap: nameOnSap,
        tax_id: taxId,
        is_vat_registered: isVatRegistered,
        house_no: houseNo,
        province: addressForm.province,
        zip_code: addressForm.zipCode,
        amphur_name: addressForm.amphurName,
        tumbon_name: addressForm.tumbonName,
        street_address: streetAddress,
        mobile_number: mobileNumber
      };

      if (logoFile) {
        params.icon = await uploadPublicImage(logoFile, name, "article");
      }

      return params;
    },
    async handleClickRemove() {
      if (this.isRemoving) return;
      try {
        await MessageBox.confirm("ยืนยันการลบ", {
          confirmButtonText: "ตกลง",
          cancelButtonText: "ยกเลิก",
        });
      } catch {
        return;
      }

      this.isRemoving = true;
      try {
        await this.removeProvider();
        this.$router.push({ name: "ProviderListPage" });
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.isRemoving = false;
      }
    },
    async removeProvider() {
      const { id } = this.$route.params;

      await baanRueJaiClient.admin.subscriptions.providers.delete(id);
    },
    async handleClickExportExcel() {
      try {
        const { id } = this.$route.params;
        const response = await baanRueJaiClient.admin.subscriptions.providers.exportExcel(id);
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `provider-${this.form.name}.xlsx`;
        link.click();
      } catch {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>
